"use client"
import { useState, useEffect, useCallback, useRef } from 'react';
import Link from 'next/link';

import * as Separator from '@radix-ui/react-separator';
import {
  Plus,
  FileText,
  HelpCircle,
  Image,
  Settings,
  ClipboardList,
  AlertCircle,
  RefreshCw,
  Wifi,
  WifiOff,
  Clock,
  CheckCircle,
  Info,
  AlertTriangle,
  Zap
} from 'lucide-react';

interface Counts {
  totalBlogs: number;
  draftBlogs: number;
  totalFAQs: number;
  totalUsers: number;
  totalMedia: number;
}

type ErrorType = 'network' | 'auth' | 'server' | 'timeout' | 'unknown';

interface ErrorState {
  type: ErrorType;
  message: string;
  details?: string;
  canRetry: boolean;
}

const quickActions = [
  {
    name: 'Create New Blog',
    description: 'Start writing a new blog post',
    icon: FileText,
    href: '/admin/blogs/new',
    color: 'bg-blue-500 hover:bg-blue-600',
    showCount: false
  },
  {
    name: 'Manage Blogs',
    description: 'View and edit blog posts',
    icon: FileText,
    href: '/admin/blogs',
    color: 'bg-green-500 hover:bg-green-600',
    showCount: true,
    countKey: 'totalBlogs' as keyof Counts
  },
  {
    name: 'Add FAQ',
    description: 'Create a new frequently asked question',
    icon: HelpCircle,
    href: '/admin/faqs/new',
    color: 'bg-purple-500 hover:bg-purple-600',
    showCount: false
  },
  {
    name: 'Manage FAQs',
    description: 'View and edit FAQs',
    icon: HelpCircle,
    href: '/admin/faqs',
    color: 'bg-indigo-500 hover:bg-indigo-600',
    showCount: true,
    countKey: 'totalFAQs' as keyof Counts
  },
  {
    name: 'Upload Media',
    description: 'Add images or videos to your library',
    icon: Image,
    href: '/admin/media',
    color: 'bg-teal-500 hover:bg-teal-600',
    showCount: true,
    countKey: 'totalMedia' as keyof Counts
  },
  {
    name: 'Manage Users',
    description: 'Add and manage user accounts',
    icon: Settings,
    href: '/admin/users',
    color: 'bg-orange-500 hover:bg-orange-600',
    showCount: true,
    countKey: 'totalUsers' as keyof Counts
  },
  {
    name: 'View Audit Logs',
    description: 'Monitor user actions and system changes',
    icon: ClipboardList,
    href: '/admin/logs',
    color: 'bg-gray-600 hover:bg-gray-700',
    showCount: false
  }
];

export default function QuickActions() {
  const [counts, setCounts] = useState<Counts | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ErrorState | null>(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [showFreshDataBanner, setShowFreshDataBanner] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const errorTypeRef = useRef<ErrorState['type'] | null>(null);

  useEffect(() => {
    errorTypeRef.current = error?.type ?? null;
  }, [error?.type]);

  const fetchCounts = useCallback(async () => {
    const getErrorState = (error: unknown, response?: Response): ErrorState => {
      if (!navigator.onLine) {
        return {
          type: 'network',
          message: 'No internet connection',
          details: 'Please check your network connection and try again.',
          canRetry: true
        };
      }

      if (response?.status === 401 || response?.status === 403) {
        return {
          type: 'auth',
          message: 'Authentication required',
          details: 'Your session has expired. Please log in again.',
          canRetry: false
        };
      }

      if (response && response.status >= 500) {
        return {
          type: 'server',
          message: 'Server error',
          details: 'The server is currently experiencing issues. Please try again later.',
          canRetry: true
        };
      }

      if (error instanceof Error && (error.name === 'AbortError' || error.message.includes('timeout'))) {
        return {
          type: 'timeout',
          message: 'Request timeout',
          details: 'The request took too long to complete. Please try again.',
          canRetry: true
        };
      }

      return {
        type: 'unknown',
        message: error instanceof Error ? error.message : 'An unexpected error occurred',
        details: 'Something went wrong while loading quick action counts.',
        canRetry: true
      };
    };

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/admin/quick-actions', {
        signal: controller.signal,
        headers: {
          'Cache-Control': 'no-cache'
        }
      });

      clearTimeout(timeoutId);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      if (data.success && data.counts) {
        setCounts(data.counts);
        setLastUpdated(new Date());
        setRetryCount(0);
      } else {
        throw new Error('Invalid response format from server');
      }
    } catch (err) {
      console.error('Error fetching counts:', err);
      const errorState = getErrorState(err, err instanceof Response ? err : undefined);
      setError(errorState);
    } finally {
      setLoading(false);
      clearTimeout(timeoutId);
    }
  }, []);

  useEffect(() => {
    if (!lastUpdated) {
      setShowFreshDataBanner(false);
      return;
    }

    setShowFreshDataBanner(true);
    const timeoutId = window.setTimeout(() => {
      setShowFreshDataBanner(false);
    }, 60000);

    return () => window.clearTimeout(timeoutId);
  }, [lastUpdated]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      fetchCounts();
    }, 0);

    // Network status monitoring
    const handleOnline = () => {
      setIsOnline(true);
      if (errorTypeRef.current === 'network') {
        fetchCounts(); // Auto retry when connection is restored
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
      setError({
        type: 'network',
        message: 'Connection lost',
        details: 'You appear to be offline. Quick actions will update when connection is restored.',
        canRetry: false
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.clearTimeout(timeoutId);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [fetchCounts]);

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
    fetchCounts();
  };

  const getErrorIcon = (errorType: ErrorType) => {
    switch (errorType) {
      case 'network':
        return <WifiOff className="h-5 w-5 text-red-500" />;
      case 'auth':
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
      case 'server':
        return <AlertCircle className="h-5 w-5 text-orange-500" />;
      case 'timeout':
        return <Clock className="h-5 w-5 text-blue-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-red-500" />;
    }
  };

  const getErrorColor = (errorType: ErrorType) => {
    switch (errorType) {
      case 'network':
        return 'border-red-200 bg-red-50';
      case 'auth':
        return 'border-yellow-200 bg-yellow-50';
      case 'server':
        return 'border-orange-200 bg-orange-50';
      case 'timeout':
        return 'border-blue-200 bg-blue-50';
      default:
        return 'border-red-200 bg-red-50';
    }
  };

  return (
    <div className="space-y-4">
      {/* Success Header with Last Updated */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <CheckCircle className="h-4 w-4 text-green-500" />
          <span className="text-sm text-gray-600">Quick actions loaded successfully</span>
        </div>
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-2">
            <Wifi className={`h-4 w-4 ${isOnline ? 'text-green-500' : 'text-red-500'}`} />
            <span className="text-xs text-gray-500">
              {isOnline ? 'Online' : 'Offline'}
            </span>
          </div>
          {lastUpdated && (
            <div className="flex items-center space-x-2 text-xs text-gray-500">
              <Clock className="h-3 w-3" />
              <span>Updated {lastUpdated.toLocaleTimeString()}</span>
            </div>
          )}
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className={`rounded-lg border p-4 ${getErrorColor(error.type)}`}>
          <div className="flex items-start space-x-3">
            {getErrorIcon(error.type)}
            <div className="flex-1">
              <h3 className="text-sm font-medium text-gray-900">{error.message}</h3>
              {error.details && (
                <p className="text-sm text-gray-700 mt-1">{error.details}</p>
              )}
              {retryCount > 0 && (
                <p className="text-xs text-gray-600 mt-2">
                  Attempt {retryCount + 1} of 3
                </p>
              )}
            </div>
            {error.canRetry && (
              <button
                onClick={handleRetry}
                disabled={retryCount >= 3}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                {retryCount >= 3 ? 'Max retries reached' : 'Try Again'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Quick Actions Container */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900">Quick Actions</h3>
              <p className="text-sm text-gray-500">Common tasks and shortcuts</p>
            </div>
            <div className="flex items-center space-x-2">
              <Zap className="h-5 w-5 text-yellow-500" />
              <span className="text-sm text-gray-600">Actions ready</span>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {quickActions.map((action) => (
            <Link
              key={action.name}
              href={action.href}
              className="w-full text-left p-4 rounded-lg border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all duration-200 group block"
            >
              <div className="flex items-center space-x-3">
                <div className={`p-2 rounded-lg ${action.color} transition-transform group-hover:scale-110 relative`}>
                  <action.icon className="h-5 w-5 text-white" />
                  {action.showCount && counts && action.countKey && counts[action.countKey] > 0 && (
                    <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium animate-pulse">
                      {action.countKey && counts[action.countKey] > 99 ? '99+' : action.countKey ? counts[action.countKey] : 0}
                    </div>
                  )}
                  {loading && action.showCount && (
                    <div className="absolute -top-2 -right-2 bg-gray-400 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium">
                      ...
                    </div>
                  )}
                </div>

                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <h4 className="text-sm font-medium text-gray-900 group-hover:text-gray-700">
                      {action.name}
                    </h4>
                    {action.showCount && counts && action.countKey && (
                      <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full">
                        {counts[action.countKey]}
                      </span>
                    )}
                    {action.showCount && loading && (
                      <div className="text-xs px-2 py-1 bg-gray-100 text-gray-400 rounded-full animate-pulse">
                        ...
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {action.description}
                  </p>
                </div>

                <Plus className="h-4 w-4 text-gray-400 group-hover:text-gray-600 transition-colors" />
              </div>
            </Link>
          ))}
        </div>

        <Separator.Root className="h-px bg-gray-200" />
        <div className="px-6 py-4 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="text-center">
              <p className="text-xs text-gray-500 mb-1">Need help?</p>
              <button className="text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors">
                View Documentation →
              </button>
            </div>
            {counts && (
              <div className="text-xs text-gray-500">
                {Object.values(counts).reduce((sum, count) => sum + count, 0)} total items
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Info Banner for Fresh Data */}
      {showFreshDataBanner && counts && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
          <div className="flex items-center space-x-2">
            <Info className="h-4 w-4 text-blue-500" />
            <span className="text-sm text-blue-800">
              Quick actions updated just now with fresh data
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
